// src/services/campaignServiceNew.js
import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';
import {
  createService,
  createFileUploadConfig,
  createNestedCollection
} from './factory';

// Create the base campaign service using the factory
const campaignServiceBase = createService('Campaign', '/campaigns', {
  fileUploadOptions: createFileUploadConfig('file', 'assets'),
  nestedCollections: [
    createNestedCollection('milestones', 'milestone', null, true),
    createNestedCollection('content'),
    createNestedCollection('team', 'member'),
    createNestedCollection('budget/transactions', 'transaction'),
    createNestedCollection('kpis', 'kpi'),
    createNestedCollection('performance')
  ],
  customEndpoints: {
    // Get active campaigns by date range
    getActiveCampaignsByDateRange: async (startDate, endDate, params = {}) => {
      const queryParams = { ...params, startDate, endDate };
      const queryString = objectToQueryString(queryParams);
      return api.get(`/campaigns/date-range${queryString}`);
    },
    
    // Get campaign performance overview
    getCampaignPerformanceOverview: async (clientId, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`/campaigns/overview/${clientId}${queryString}`);
    },
    
    // Get campaigns for a parent entity (client, brand)
    getFromParent: async (parentId, parentType, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`/${parentType}s/${parentId}/campaigns${queryString}`);
    }
  }
});

// Create backwards-compatible exports that use the factory methods underneath
export const getCampaigns = async (params = {}) => {
  return campaignServiceBase.getAll(params);
};

export const getCampaignById = async (id) => {
  return campaignServiceBase.getById(id);
};

export const createCampaign = async (campaignData) => {
  return campaignServiceBase.create(campaignData);
};

export const updateCampaign = async (id, campaignData) => {
  return campaignServiceBase.update(id, campaignData);
};

export const deleteCampaign = async (id) => {
  return campaignServiceBase.delete(id);
};

export const getBrandCampaigns = async (brandId, params = {}) => {
  return campaignServiceBase.getFromParent(brandId, 'brand', params);
};

export const getClientCampaigns = async (clientId, params = {}) => {
  return campaignServiceBase.getFromParent(clientId, 'client', params);
};

// Upload campaign asset
export const uploadCampaignAsset = async (campaignId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    return await api.postFiles(`/campaigns/${campaignId}/assets`, formData);
  } catch (error) {
    console.error('Error uploading campaign asset:', error);
    throw error;
  }
};

// Campaign milestone methods
export const addCampaignMilestone = async (campaignId, milestone) => {
  return campaignServiceBase.addMilestone(campaignId, milestone);
};

export const updateCampaignMilestone = async (campaignId, milestoneId, updates) => {
  return campaignServiceBase.updateMilestone(campaignId, milestoneId, updates);
};

// Update campaign status
export const updateCampaignStatus = async (campaignId, status) => {
  return campaignServiceBase.updateStatus(campaignId, status);
};

// Add budget transaction
export const addBudgetTransaction = async (campaignId, transaction) => {
  return campaignServiceBase.addTransaction(campaignId, transaction);
};

// Update campaign KPIs
export const updateCampaignKPIs = async (campaignId, kpiUpdates) => {
  return api.put(`/campaigns/${campaignId}/kpis`, { kpis: kpiUpdates });
};

// Get campaign content
export const getCampaignContent = async (campaignId, params = {}) => {
  return campaignServiceBase.getContent(campaignId, params);
};

// Get campaign performance
export const getCampaignPerformance = async (campaignId, params = {}) => {
  return campaignServiceBase.getPerformance(campaignId, params);
};

// Update campaign team
export const updateCampaignTeam = async (campaignId, team) => {
  return api.put(`/campaigns/${campaignId}/team`, { team });
};

// Get active campaigns by date range
export const getActiveCampaignsByDateRange = async (startDate, endDate, params = {}) => {
  return campaignServiceBase.getActiveCampaignsByDateRange(startDate, endDate, params);
};

// Get campaign performance overview
export const getCampaignPerformanceOverview = async (clientId, params = {}) => {
  return campaignServiceBase.getCampaignPerformanceOverview(clientId, params);
};

// Create a full service object that includes all methods
const campaignService = {
  getCampaigns,
  getCampaignById,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getBrandCampaigns,
  getClientCampaigns,
  uploadCampaignAsset,
  addCampaignMilestone,
  updateCampaignMilestone,
  updateCampaignStatus,
  addBudgetTransaction,
  updateCampaignKPIs,
  getCampaignContent,
  getCampaignPerformance,
  updateCampaignTeam,
  getActiveCampaignsByDateRange,
  getCampaignPerformanceOverview
};

export default campaignService;